import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  Card,
  CardBody,
  Spinner,
  Center,
  Badge,
  useToast,
  Divider,
  Flex,
  Image,
} from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { ChevronLeftIcon } from "@chakra-ui/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const NoticeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchNoticeDetail();
    } else {
      navigate("/notice");
    }
  }, [id, navigate]);

  const fetchNoticeDetail = async () => {
    try {
      setLoading(true);
      console.log("Fetching notice detail for ID:", id);

      const { data, error } = await supabase
        .from("notice")
        .select("id, title, content, created_at, is_visible")
        .eq("id", id)
        .eq("is_visible", true)
        .single();

      console.log("Supabase response:", { data, error });

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }

      if (!data) {
        console.log("No notice data found");
        navigate("/notice");
        return;
      }

      console.log("Notice data loaded:", data);
      setNotice(data);
    } catch (error) {
      console.error("공지사항 상세 조회 실패:", error);
      navigate("/notice");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Center minH="60vh">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  // 데이터가 없으면 로딩 중이 아니므로 리다이렉트
  if (!notice) {
    // 이미 fetchNoticeDetail에서 리다이렉트가 처리되므로 여기서는 null 반환
    return null;
  }

  return (
    <Box minH="100vh" bg="gray.50">
      <Container maxW="container.lg" py={8}>
        <VStack spacing={6} align="stretch">
          {/* 뒤로가기 버튼 */}
          <Box>
            <Button
              leftIcon={<ChevronLeftIcon />}
              variant="ghost"
              color="blue.600"
              onClick={() => navigate("/notice")}
              _hover={{ bg: "blue.50" }}
            >
              공지사항 목록으로
            </Button>
          </Box>

          {/* 공지사항 상세 내용 */}
          <Card>
            <CardBody p={8}>
              <VStack spacing={6} align="stretch">
                {/* 제목과 날짜 */}
                <Flex justify="space-between" align="center" mb={4}>
                  <Heading size="xl" color="gray.800">
                    {notice.title}
                  </Heading>
                  <Badge colorScheme="blue" variant="subtle" size="lg">
                    {formatDate(notice.created_at)}
                  </Badge>
                </Flex>

                <Divider />

                {/* 마크다운 내용 */}
                <Box>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({ children }) => (
                        <Heading as="h1" size="lg" color="gray.800" mb={4}>
                          {children}
                        </Heading>
                      ),
                      h2: ({ children }) => (
                        <Heading as="h2" size="md" color="gray.800" mb={3}>
                          {children}
                        </Heading>
                      ),
                      h3: ({ children }) => (
                        <Heading as="h3" size="sm" color="gray.800" mb={2}>
                          {children}
                        </Heading>
                      ),
                      p: ({ children }) => (
                        <Text
                          fontSize="lg"
                          color="gray.700"
                          lineHeight="1.8"
                          mb={4}
                        >
                          {children}
                        </Text>
                      ),
                      ul: ({ children }) => (
                        <Box as="ul" pl={6} mb={4}>
                          {children}
                        </Box>
                      ),
                      ol: ({ children }) => (
                        <Box as="ol" pl={6} mb={4}>
                          {children}
                        </Box>
                      ),
                      li: ({ children }) => (
                        <Text
                          as="li"
                          fontSize="lg"
                          color="gray.700"
                          lineHeight="1.6"
                          mb={1}
                        >
                          {children}
                        </Text>
                      ),
                      strong: ({ children }) => (
                        <Text as="span" fontWeight="bold" color="gray.800">
                          {children}
                        </Text>
                      ),
                      em: ({ children }) => (
                        <Text as="span" fontStyle="italic" color="gray.700">
                          {children}
                        </Text>
                      ),
                      code: ({ children }) => (
                        <Box
                          as="code"
                          bg="gray.100"
                          px={2}
                          py={1}
                          borderRadius="md"
                          fontSize="sm"
                          fontFamily="mono"
                          color="gray.800"
                        >
                          {children}
                        </Box>
                      ),
                      blockquote: ({ children }) => (
                        <Box
                          as="blockquote"
                          borderLeft="4px solid"
                          borderColor="blue.200"
                          pl={4}
                          py={2}
                          bg="blue.50"
                          borderRadius="md"
                          mb={4}
                        >
                          {children}
                        </Box>
                      ),
                      a: ({ href, children }) => (
                        <Text
                          as="a"
                          href={href}
                          color="blue.600"
                          textDecoration="underline"
                          _hover={{ color: "blue.800" }}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {children}
                        </Text>
                      ),
                      img: ({ src, alt }) => (
                        <Box my={4} textAlign="center">
                          <Image
                            src={src}
                            alt={alt || "이미지"}
                            maxW="100%"
                            maxH="500px"
                            objectFit="contain"
                            borderRadius="lg"
                            boxShadow="md"
                          />
                        </Box>
                      ),
                    }}
                  >
                    {notice.content}
                  </ReactMarkdown>
                </Box>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Box>
  );
};

export default NoticeDetail;
